import React, { memo, useState, useCallback, useEffect, dispatch } from 'react';
import PropTypes from 'prop-types';
import StyleConstants from 'js/StyleConstants';

import MapPolygon from 'js/components/MapObjects/MapPolygon';
import MapOverlay from 'js/components/MapObjects/MapOverlay';
import MapPopup from 'js/components/MapObjects/MapPopup';
import FieldPopupContent from 'js/components/MapView/FieldPopupContent';
import ViewModeConstants from 'js/constants/ViewModeConstants';

import { voidFunc } from 'js/constants/PropTypeUtils';
import { getFieldCenter } from '../../helpers/MapsUtils';
import { useMapViewContext } from '../MapView/MapViewContext';
import MapFieldHole from './MapFieldHole';

const MapField = (props: MapField.propTypes) => {
  const mapViewContext = useMapViewContext();

  const [coordinates, setCoordinates] = useState(
    props.field.polygon.coordinates.map((ring) => {
      return ring.map((coord) => {
        return {
          lng: coord[0],
          lat: coord[1],
        };
      });
    })
  );

  const [hovered, setHovered] = useState(false);

  const onClick = useCallback(
    (event) => {
      mapViewContext.onSelectField(props.field, event);
    },
    [mapViewContext.onSelectField, props.field]
  );

  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    if (!mapViewContext.disableFieldInteraction) {
      setHovered(false);
    }
  }, []);

  if (!props.field) {
    return;
  }

  let strokeWeight = 2;
  let strokeColor = 'rgb(224,210,61)';
  const fillColor = 'white';

  if (hovered && !props.selected) {
    strokeColor = StyleConstants.palette.primary.main;
    strokeWeight = 4;
  } else if (props.selected) {
    strokeColor = 'white';
    strokeWeight = 6;
  }
  const validImage =
    props.fieldImage && (props.fieldImage[props.imageType] || props.fieldImage.url);

  useEffect(() => {
    setCoordinates(
      props.field.polygon.coordinates[0].map((coord) => ({
        lng: coord[0],
        lat: coord[1],
      }))
    );
  }, [props.field.polygon.coordinates]);

  return (
    <MapPolygon
      fillOpacity={0.0}
      fillColor={fillColor}
      strokeColor={strokeColor}
      strokeWeight={strokeWeight}
      disabled={mapViewContext.disableFieldInteraction}
      coordinates={coordinates}
      selected={props.selected}
      onClick={onClick}
      zIndex={validImage ? 100 : 0}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {validImage && (
        <MapOverlay
          zIndex={validImage ? 100 : 0}
          fieldImage={props.fieldImage}
          imageType={props.imageType}
        />
      )}
      {props.selected && props.viewMode === ViewModeConstants.OVERVIEW && (
        <MapPopup
          clickable={true}
          hasPin={true}
          selected={true}
          shown={true}
          onClickAway={mapViewContext.onClickAway}
          position={getFieldCenter(props.field)}
          style={{ zIndex: 9 }}
        >
          <FieldPopupContent
            fieldImage={props.fieldImage}
            survey={props.survey}
            imageType={props.imageType}
            field={props.field}
            satelliteImagerySelected={!props.onlyOutline}
            yieldForField={props.yieldForField}
            selectedLayer={props.selectedLayer}
          />
        </MapPopup>
      )}
      {props.fieldHoles &&
        props.fieldHoles.map((fieldHole, index) => (
          <MapFieldHole
            fieldHole={fieldHole}
            key={index}
            dispatch={dispatch}
            fieldHoleDisabled={true}
          />
        ))}
    </MapPolygon>
  );
};

MapField.propTypes = {
  fieldImage: PropTypes.object,
  survey: PropTypes.array,
  imageType: PropTypes.string,
  coordinates: PropTypes.arrayOf(PropTypes.object).isRequired,
  field: PropTypes.object,
  strokeWeight: PropTypes.number,
  selected: PropTypes.bool,
  onlyOutline: PropTypes.bool,
};

MapField.defaultProps = {
  onClose: voidFunc,
  selected: false,
  viewMode: ViewModeConstants.OVERVIEW,
  imageType: null,
  fieldImage: null,
  onlyOutline: false,
  coordinates: [],
};

export default memo(MapField);
